// =============================================================================
// VerifyEmailPage — confirm email ownership from an emailed link
// =============================================================================
// Reads the single-use token from `?token=...` and posts it to the public
// verify-email endpoint once on mount. Verification is non-blocking for the
// account, so this page is purely confirmatory.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '@/lib/api';
import { Button, Card, Spinner } from '@tkpod/ui';

type Status = 'verifying' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
  const [message, setMessage] = useState<string | null>(
    token ? null : 'This verification link is incomplete.',
  );
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true;

    async function verify() {
      try {
        await api.post('/api/v1/auth/verify-email', { token });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setMessage(
          err instanceof Error
            ? err.message
            : 'This verification link is invalid or has expired.',
        );
      }
    }

    void verify();
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
            <span className="text-lg font-bold text-white">TK</span>
          </div>
        </div>

        <Card>
          <div className="p-6 text-center">
            {status === 'verifying' && (
              <>
                <Spinner size="lg" />
                <p className="mt-4 text-sm text-text-secondary">
                  Verifying your email…
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                  <svg
                    className="h-6 w-6 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m4.5 12.75 6 6 9-13.5"
                    />
                  </svg>
                </div>
                <h2 className="mb-2 text-lg font-semibold text-text-primary">
                  Email verified
                </h2>
                <p className="mb-6 text-sm text-text-secondary">
                  Thanks — your email address has been confirmed.
                </p>
                <Link to="/dashboard">
                  <Button className="w-full">Go to dashboard</Button>
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
                  <svg
                    className="h-6 w-6 text-danger"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                    />
                  </svg>
                </div>
                <h2 className="mb-2 text-lg font-semibold text-text-primary">
                  Verification failed
                </h2>
                <p className="mb-6 text-sm text-text-secondary">
                  {message ?? 'This verification link is invalid or has expired.'}
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    Back to sign in
                  </Button>
                </Link>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
